import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { fetchData } from '../utils/apiHelpers';
import { useFormBuilder } from '../utils/useFormBuilder';
import { create, deleteOne, getAll, update } from '../api';
import ContractorWizardModal from './common/ContractorWizardModal';
import { useContractorWizard } from '../hook/useContractorWizard';

const COUNTRY_CODES = [
  { value: '+61', label: 'Australia (+61)' },
  { value: '+64', label: 'New Zealand (+64)' },
  { value: '+1', label: 'United States/Canada (+1)' },
  { value: '+44', label: 'United Kingdom (+44)' },
  { value: '+65', label: 'Singapore (+65)' },
  { value: '+91', label: 'India (+91)' },
];

const DISPUTE_NOTE = '[GLOBAL_LINK_DISPUTE] Potential phone or email collision. User declined linking.';
const resolveProfileImageUrl = (profileImage) => {
  if (!profileImage) return `${process.env.REACT_APP_SPACES_BASE}/user.png`;
  if (String(profileImage).startsWith('http://') || String(profileImage).startsWith('https://')) return profileImage;
  return `${process.env.REACT_APP_SPACES_BASE}/${profileImage}`;
};

const isValidAustralianMobile = (phoneNational) => {
  if (!phoneNational) return false;

  const digits = phoneNational.replace(/\s+/g, '');

  if (!/^\d{10}$/.test(digits)) return false;
  if (!digits.startsWith('04')) return false;

  return true;
};

const isValidInternationalMobileLength = (phoneNational) => {
  if (!phoneNational) return false;
  const digits = phoneNational.replace(/\s+/g, '');
  return /^\d{8,15}$/.test(digits);
};

const DEFAULT_WIZARD = {
  mode: 'create',
  step: 1,
  editingId: null,
  validationLocked: false,
  selectedMatchId: null,
  matchResult: { status: 'idle', candidates: [] },
  local: {
    profileImage: '',
    name: '',
    supplierId: null,
    phoneCountryCode: '+61',
    phoneNational: '',
    email: '',
    notes: '',
  },
  global: {
    profileImage: '',
    name: '',
    supplierId: null,
    phone: '',
    email: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    postcode: '',
    country: '',
  },
};

const normalizePhoneDigits = (phoneValue) => String(phoneValue || '').replace(/\D/g, '');

const ContractorsLayer = () => {
  const [contractors, setContractors] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showWizard, setShowWizard] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [wizard, setWizard] = useState(DEFAULT_WIZARD);
  const [editForm, setEditForm] = useState(DEFAULT_WIZARD.local);
  const [wizardErrors, setWizardErrors] = useState({});
  const [editErrors, setEditErrors] = useState({});
  const [editEditingId, setEditEditingId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const profileFileInputRef = useRef(null);
  const editProfileFileInputRef = useRef(null);

  const supplierOptions = useMemo(
    () => suppliers.map((supplier) => ({ value: supplier.id, label: supplier.name })),
    [suppliers],
  );

  const fetchContractors = useCallback(async () => {
    const response = await getAll('contractors');
    const nextContractors = Array.isArray(response)
      ? response
      : Array.isArray(response?.data)
        ? response.data
        : [];
    setContractors(nextContractors);
  }, []);

  const wizardApi = useContractorWizard({
    contractors,
    suppliers,
    onSaved: async () => {
      await fetchContractors();
    },
  });

  const localFormFields = useMemo(() => ([
    { field: 'name', label: 'Name', type: 'text', size: 12, required: true, maxLength: 100 },
    {
      field: 'supplierId', label: 'Select Supplier', type: 'select', size: 12, required: true,
      options: supplierOptions, isClearable: true,
    },
    { field: 'phoneCountryCode', label: 'Country Calling Code', type: 'select', size: 4, options: COUNTRY_CODES, isClearable: false },
    {
      field: 'phoneNational', label: 'Phone', type: 'text', size: 8,
      onChange: (event) => {
        const nextValue = (event.target.value || '').replace(/[^\d ]/g, '');
        setWizard((prev) => ({ ...prev, local: { ...prev.local, phoneNational: nextValue } }));
      },
      onBlur: () => {
        setWizard((prev) => ({ ...prev, local: { ...prev.local, phoneNational: (prev.local.phoneNational || '').replace(/\s+/g, ' ').trim() } }));
      },
    },
    { field: 'email', label: 'Email', type: 'text', size: 12 },
    { field: 'notes', label: 'Notes', type: 'textarea', size: 12 },
  ]), [supplierOptions]);

  const editFormFields = useMemo(() => ([
    {
      field: 'name', label: 'Name', type: 'text', size: 12, required: true, maxLength: 100,
    },
    {
      field: 'supplierId', label: 'Supplier', type: 'select', size: 12, required: true,
      options: supplierOptions, isClearable: true,
    },
    { field: 'phoneCountryCode', label: 'Country Calling Code', type: 'select', size: 4, options: COUNTRY_CODES, isClearable: false },
    {
      field: 'phoneNational', label: 'Phone', type: 'text', size: 8,
      onChange: (event) => {
        const nextValue = (event.target.value || '').replace(/[^\d ]/g, '');
        setEditForm((prev) => ({ ...prev, phoneNational: nextValue }));
      },
      onBlur: () => {
        setEditForm((prev) => ({ ...prev, phoneNational: (prev.phoneNational || '').replace(/\s+/g, ' ').trim() }));
      },
    },
    { field: 'email', label: 'Email', type: 'text', size: 12 },
    { field: 'notes', label: 'Notes', type: 'textarea', size: 12 },
  ]), [supplierOptions]);

  const setWizardLocalForm = useCallback((next) => {
    setWizard((prev) => ({
      ...prev,
      local: typeof next === 'function' ? next(prev.local) : next,
    }));
  }, []);

  const { renderField: renderLocalField } = useFormBuilder(localFormFields, wizard.local, setWizardLocalForm, wizardErrors);
  const { renderField: renderEditField } = useFormBuilder(editFormFields, editForm, setEditForm, editErrors);

  const wizardPhoneFields = useMemo(
    () => localFormFields.filter((field) => ['phoneCountryCode', 'phoneNational'].includes(field.field)),
    [localFormFields],
  );

  const wizardDetailsFields = useMemo(
    () => localFormFields.filter((field) => ['name', 'supplierId', 'email', 'notes'].includes(field.field)),
    [localFormFields],
  );

  const getLocalFieldConfig = (fieldName, overrides = {}) => {
    const fieldConfig = localFormFields.find((item) => item.field === fieldName);
    if (!fieldConfig) return null;
    return { ...fieldConfig, ...overrides };
  };

  useEffect(() => {
    fetchContractors().catch((error) => {
      console.error('Error fetching contractors:', error);
    });
    fetchData(getAll, { apiName: 'suppliers' }, (res) => setSuppliers(Array.isArray(res?.data) ? res.data : []));
  }, [fetchContractors]);

  const filteredContractors = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return contractors;
    return contractors.filter((contractor) => {
      const supplierName = contractor.supplier?.name || '';
      return [contractor.name, supplierName, contractor.phone, contractor.email]
        .some((value) => String(value || '').toLowerCase().includes(term));
    });
  }, [contractors, searchTerm]);

  const normalizePhone = (countryCode, phoneNational) => {
    const digitsNationalRaw = (phoneNational || '').replace(/\s+/g, '');
    let digitsNational = digitsNationalRaw;
    if ((countryCode || '+61') === '+61' && digitsNational.startsWith('0')) {
      digitsNational = digitsNational.slice(1);
    }
    const phoneE164 = digitsNational ? `${countryCode || '+61'}${digitsNational}` : null;
    return { phoneE164, phoneDigitsKey: phoneE164 ? phoneE164.replace(/\D/g, '') : null };
  };

  const normalizeEmail = (email) => (email || '').trim().toLowerCase() || null;

  const openCreateWizard = () => {
    setWizard({ ...DEFAULT_WIZARD, mode: 'create' });
    setWizardErrors({});
    setShowWizard(true);
  };

  const parsePhoneForEdit = (phoneE164) => {
    if (!phoneE164) return { phoneCountryCode: '+61', phoneNational: '' };
    const cleaned = String(phoneE164).replace(/\s+/g, '');
    const matchedCode = COUNTRY_CODES.map((item) => item.value).sort((a, b) => b.length - a.length).find((code) => cleaned.startsWith(code));
    if (!matchedCode) return { phoneCountryCode: '+61', phoneNational: cleaned.replace(/^\+/, '') };
    const nationalValue = cleaned.slice(matchedCode.length);
    const phoneNational = matchedCode === '+61' && nationalValue.length === 9 ? `0${nationalValue}` : nationalValue;
    return { phoneCountryCode: matchedCode, phoneNational };
  };

  const openEditForm = (contractor) => {
    const parsedPhone = parsePhoneForEdit(contractor.phone);
    setEditForm({
      profileImage: contractor.profileImage || '',
      name: contractor.name || '',
      supplierId: contractor.supplierId || null,
      phoneCountryCode: parsedPhone.phoneCountryCode,
      phoneNational: parsedPhone.phoneNational,
      email: contractor.email || '',
      notes: contractor.notes || '',
    });
    setEditEditingId(contractor.id);
    setEditErrors({});
    setShowEditModal(true);
  };

  const applyMatchedCandidateToLocal = (candidate, currentLocal) => {
    if (!candidate) return currentLocal;
    return {
      ...currentLocal,
      name: currentLocal.name || candidate.name || '',
      email: currentLocal.email || candidate.email || '',
      profileImage: currentLocal.profileImage || candidate.profileImage || '',
      supplierId: currentLocal.supplierId || candidate.suggestedLocalSupplierId || null,
    };
  };

  const runValidationStep = async () => {
    const localPhone = (wizard.local.phoneNational || '').trim();
    if (!localPhone) {
      setWizardErrors({ phoneNational: 'Enter a phone number.' });
      return;
    }

    if (wizard.local.phoneCountryCode === '+61') {
      if (!isValidAustralianMobile(localPhone)) {
        setWizardErrors({ phoneNational: 'Enter a valid Australian mobile number (04xxxxxxxx).' });
        return;
      }
    } else if (!isValidInternationalMobileLength(localPhone)) {
      setWizardErrors({ phoneNational: 'Enter a valid mobile number.' });
      return;
    }

    const { phoneDigitsKey, phoneE164 } = normalizePhone(wizard.local.phoneCountryCode, localPhone);
    const localPhoneMatch = contractors.find((contractor) => normalizePhoneDigits(contractor.phone) === phoneDigitsKey);

    if (localPhoneMatch) {
      setWizardErrors({
        phoneNational: 'Contractor already exists.',
      });
      return;
    }

    setIsValidating(true);
    try {
      const candidates = await create('contractors/validate-global', {
        phoneDigitsKey: phoneDigitsKey || undefined,
      });
      const list = Array.isArray(candidates) ? candidates : [];
      const selected = list.length === 1 ? list[0] : null;

      setWizard((prev) => ({
        ...prev,
        step: 2,
        selectedMatchId: selected?.id || null,
        matchResult: { status: list.length === 0 ? 'none' : list.length === 1 ? 'single' : 'multiple', candidates: list },
        local: applyMatchedCandidateToLocal(selected, {
          ...prev.local,
          phoneCountryCode: prev.local.phoneCountryCode || '+61',
          phoneNational: prev.local.phoneNational,
        }),
        global: {
          ...prev.global,
          name: selected?.name || prev.global.name || prev.local.name,
          phone: phoneE164 || '',
          email: selected?.email || normalizeEmail(prev.local.email) || '',
          profileImage: selected?.profileImage || prev.global.profileImage || prev.local.profileImage,
          supplierId: selected?.supplierId || prev.global.supplierId || prev.local.supplierId,
        },
      }));
      setWizardErrors({});
    } catch (error) {
      alert(error.message || 'Unable to validate against global contractors.');
    } finally {
      setIsValidating(false);
    }
  };

  const buildPayload = () => {
    const selectedCandidate = wizard.matchResult.candidates.find((candidate) => candidate.id === wizard.selectedMatchId);
    const { phoneE164 } = normalizePhone(wizard.local.phoneCountryCode, wizard.local.phoneNational);
    const email = normalizeEmail(wizard.local.email || selectedCandidate?.email);

    const hasMatch = wizard.matchResult.status === 'single' || wizard.matchResult.status === 'multiple';
    const mode = wizard.matchResult.status === 'none' ? 'create_new_global' : hasMatch ? 'link_existing' : 'local_only';

    return {
      mode,
      matchedGlobalId: mode === 'link_existing' ? wizard.selectedMatchId : null,
      supplierId: wizard.local.supplierId,
      profileImage: wizard.local.profileImage || selectedCandidate?.profileImage || '',
      name: (wizard.local.name || selectedCandidate?.name || '').trim(),
      phone: phoneE164,
      phoneCountryCode: wizard.local.phoneCountryCode,
      phoneNational: wizard.local.phoneNational,
      email,
      notes: mode === 'local_only' ? `${wizard.local.notes || ''}${wizard.local.notes ? '\n' : ''}${DISPUTE_NOTE}` : wizard.local.notes,
      globalData: mode === 'create_new_global' ? {
        profileImage: wizard.local.profileImage || '',
        name: (wizard.local.name || '').trim(),
        supplierId: wizard.local.supplierId,
        phone: phoneE164,
        email,
      } : null,
      unlinkAndRelink: Boolean(wizard.mode === 'edit' && wizard.validationLocked === false && mode !== 'link_existing'),
    };
  };

  const handleSave = async () => {
    const payload = buildPayload();
    const nextErrors = {};

    const localPhone = (wizard.local.phoneNational || '').trim();
    if (wizard.local.phoneCountryCode === '+61' && localPhone && !isValidAustralianMobile(localPhone)) {
      nextErrors.phoneNational = 'Enter a valid Australian mobile number (04xxxxxxxx).';
    }
    if (wizard.local.phoneCountryCode !== '+61' && localPhone && !isValidInternationalMobileLength(localPhone)) {
      nextErrors.phoneNational = 'Enter a valid mobile number.';
    }

    if (!payload.name && payload.mode === 'create_new_global') nextErrors.name = 'Name is required.';
    if (!payload.supplierId) nextErrors.supplierId = 'Supplier is required.';
    if (!payload.phone) {
      nextErrors.phoneNational = nextErrors.phoneNational || 'Enter a phone number.';
    }
    if (payload.mode === 'link_existing' && !payload.matchedGlobalId) nextErrors.selectedMatchId = 'Select a global contractor to link.';
    if (payload.mode === 'create_new_global' && !(payload.globalData?.name || '').trim()) nextErrors.globalName = 'Global name is required.';
    if (Object.keys(nextErrors).length) {
      setWizardErrors(nextErrors);
      return;
    }

    setIsSaving(true);
    try {
      if (wizard.editingId) {
        await update('contractors', wizard.editingId, payload);
      } else {
        await create('contractors', payload);
      }
      await fetchContractors();
      setShowWizard(false);
    } catch (error) {
      alert(error.message || 'Unable to save contractor.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLocalEditSave = async () => {
    const nextErrors = {};
    const { phoneE164 } = normalizePhone(editForm.phoneCountryCode, editForm.phoneNational);
    const localPhone = (editForm.phoneNational || '').trim();

    if (!editForm.name?.trim()) nextErrors.name = 'Name is required.';
    if (!editForm.supplierId) nextErrors.supplierId = 'Supplier is required.';
    if (!phoneE164) nextErrors.phoneNational = 'Enter a phone number.';
    if (editForm.phoneCountryCode === '+61' && localPhone && !isValidAustralianMobile(localPhone)) {
      nextErrors.phoneNational = 'Enter a valid Australian mobile number (04xxxxxxxx).';
    }
    if (editForm.phoneCountryCode !== '+61' && localPhone && !isValidInternationalMobileLength(localPhone)) {
      nextErrors.phoneNational = 'Enter a valid mobile number.';
    }

    if (Object.keys(nextErrors).length) {
      setEditErrors(nextErrors);
      return;
    }

    setIsSaving(true);
    try {
      await update('contractors', editEditingId, {
        localOnlyUpdate: true,
        mode: 'local_only',
        matchedGlobalId: null,
        supplierId: editForm.supplierId,
        profileImage: editForm.profileImage || '',
        name: (editForm.name || '').trim(),
        phone: phoneE164,
        phoneCountryCode: editForm.phoneCountryCode,
        phoneNational: editForm.phoneNational,
        email: normalizeEmail(editForm.email),
        notes: editForm.notes || '',
      });
      await fetchContractors();
      setShowEditModal(false);
      setEditEditingId(null);
    } catch (error) {
      alert(error.message || 'Unable to save contractor.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteOne('contractors', deleteTarget.id);
      await fetchContractors();
      setDeleteTarget(null);
    } catch (error) {
      alert(error.message || 'Unable to delete contractor.');
    }
  };

  const uploadProfileImage = async (file, isEdit = false) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${process.env.REACT_APP_API_URL}/upload/modelimages`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || !body?.key) throw new Error(body?.message || 'Unable to upload image.');

    if (isEdit) {
      setEditForm((prev) => ({ ...prev, profileImage: body.key }));
    } else {
      setWizard((prev) => ({ ...prev, local: { ...prev.local, profileImage: body.key } }));
    }
  };

  const selectedCandidate = wizard.matchResult.candidates.find((candidate) => candidate.id === wizard.selectedMatchId);

  return (
    <div className='card h-100 p-0 rounded-0 overflow-hidden'>
      <div className='card-header border-bottom bg-base py-16 px-24 d-flex justify-content-between'>
        <input type='text' className='bg-base form-control w-auto' placeholder='Search contractors…' value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} />
        <div className='d-flex align-items-center gap-2'>
          <button className='btn btn-outline-primary text-sm btn-sm px-16 py-12 radius-4'>Export</button>
          <button className='btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2' onClick={wizardApi.openWizard}>
            <Icon icon='ic:baseline-plus' className='icon text-xl line-height-1' />
            Add Contractor
          </button>
        </div>
      </div>

      <div className='card-body p-0'>
        <div className='table-responsive'>
          <table className='table bordered-table mb-3'>
            <thead><tr><th>Profile</th><th>Name</th><th>Supplier</th><th>Phone</th><th>Email</th><th>Actions</th></tr></thead>
            <tbody>
              {filteredContractors.map((contractor) => (
                <tr key={contractor.id}>
                  <td style={{ width: '77px' }}><img src={resolveProfileImageUrl(contractor.profileImage)} alt='Contractor' style={{ width: '45px', height: '45px', objectFit: 'cover', borderRadius: '50%', border: '1px solid #ddd' }} /></td>
                  <td>
                    {contractor.name || '-'}
                    {!contractor.globalId && <span className='ms-2 badge bg-warning-focus text-warning-main'>Unlinked</span>}
                  </td>
                  <td>{contractor.supplier?.name || '-'}</td>
                  <td>{contractor.phone || '-'}</td>
                  <td>{contractor.email || '-'}</td>
                  <td><div className='d-flex gap-2'>
                    <button className='w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center' onClick={() => openEditForm(contractor)}><Icon icon='ic:baseline-edit' className='icon text-xl line-height-1' /></button>
                    <button className='w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center' onClick={() => setDeleteTarget(contractor)}><Icon icon='ic:baseline-delete-outline' className='icon text-xl line-height-1' /></button>
                  </div></td>
                </tr>
              ))}
              {!filteredContractors.length && (
                <tr>
                  <td colSpan={6} className='text-center py-4'>No contractors found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ContractorWizardModal wizardApi={wizardApi} />


      {showEditModal && (
        <div className='modal show d-block' tabIndex='-1'>
          <div className='modal-dialog modal-lg'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>Edit Contractor</h5>
                <button type='button' className='btn-close' onClick={() => { setShowEditModal(false); setEditEditingId(null); }} />
              </div>
              <div className='modal-body'>
                <div className='mb-3'>
                  <label className='form-label mb-8'>Profile Image</label>
                  {editForm.profileImage && (
                    <div className='mb-12'>
                      <img
                        src={resolveProfileImageUrl(editForm.profileImage)}
                        alt='Selected profile'
                        style={{ width: '64px', height: '64px', objectFit: 'cover', borderRadius: '50%', border: '1px solid #ddd' }}
                      />
                    </div>
                  )}
                  <div className='d-flex gap-2'>
                    <button type='button' className='btn btn-outline-primary btn-sm' onClick={() => editProfileFileInputRef.current?.click()}>
                      {editForm.profileImage ? 'Change Image' : 'Select Image'}
                    </button>
                    {editForm.profileImage && (
                      <button type='button' className='btn btn-outline-secondary btn-sm' onClick={() => setEditForm((prev) => ({ ...prev, profileImage: '' }))}>
                        Remove
                      </button>
                    )}
                  </div>
                  <input
                    ref={editProfileFileInputRef}
                    type='file'
                    className='d-none'
                    accept='image/*'
                    onChange={async (event) => {
                      const file = event.target.files?.[0];
                      if (!file) return;
                      try {
                        await uploadProfileImage(file, true);
                      } catch (error) {
                        alert(error.message || 'Unable to upload image.');
                      }
                    }}
                  />
                </div>
                <div className='row g-3'>
                  {['name', 'supplierId', 'phoneCountryCode', 'phoneNational', 'email', 'notes']
                    .map((fieldName) => editFormFields.find((field) => field.field === fieldName))
                    .filter(Boolean)
                    .map(renderEditField)}
                </div>
              </div>
              <div className='modal-footer'>
                <button className='btn btn-secondary' onClick={() => { setShowEditModal(false); setEditEditingId(null); }}>Cancel</button>
                <button className='btn btn-primary d-inline-flex align-items-center gap-2' onClick={handleLocalEditSave} disabled={isSaving}>
                  {isSaving && <span className='spinner-border spinner-border-sm' />}Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className='modal show d-block' tabIndex='-1'>
          <div className='modal-dialog'><div className='modal-content'><div className='modal-header'><h5 className='modal-title'>Delete Contractor</h5><button type='button' className='btn-close' onClick={() => setDeleteTarget(null)} /></div><div className='modal-body'>Are you sure you want to delete <strong>{deleteTarget.name || 'this contractor'}</strong>?</div><div className='modal-footer'><button className='btn btn-secondary' onClick={() => setDeleteTarget(null)}>Cancel</button><button className='btn btn-danger' onClick={handleDelete}>Delete</button></div></div></div>
        </div>
      )}
    </div>
  );
};

export default ContractorsLayer;
